export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/monitoramento': ['gestao_vendas', 'gestao_vendas_vendedor'],
  '/dashboard': ['painel_operacional'],
  '/agenda': ['agenda'],
  '/chat': ['chat'],
  '/pipeline': ['fluxo_vendas'],
  '/contacts': ['contatos'],
  /* Organizações é o mesmo cadastro de quem se atende, visto pela empresa
     em vez da pessoa — herda a permissão de contatos. Não existe permissão
     `organizacoes` na base, e inventar uma deixaria a tela aberta para todos
     (rota sem chave cai no `return true`). */
  '/organizations': ['contatos'],
  /* Chave própria, criada pela migration AddNovaEntidadeAssinaturaCliente: a
     tela é de um cadastro que só algumas empresas têm. Rota fora deste mapa
     cai no `return true` e abriria para todos. */
  '/assinaturas': ['assinaturas'],
  '/campanhas': ['marketing_disparador'],
  '/campaign-templates': ['marketing_templates'],
  /* Chave própria, criada pelo script `add_ads_permission.sql`: a tela mostra
     custo de mídia, que é informação de quem paga. Herdar `gestao_conexoes`
     misturaria "pode plugar a conta" com "pode ver quanto ela custou", e deixar
     a rota fora deste mapa cai no `return true` — abriria para todo mundo.
     A MESMA string é o claim exigido pela AnunciosPolicy na API. */
  '/anuncios': ['anuncios'],
  /* Uma tela só, com as DUAS permissões antigas: quem já via qualquer um dos
     dois relatórios continua entrando. Uma permissão `relatorios` própria seria
     mais limpa, mas ela não existe na base — criar exige seed, e rota sem chave
     no mapa cai no `return true`, o que abriria a tela para todos.
     Como `canAccessRoute` herda do caminho-mãe, isto já cobre `/relatorios/novo`,
     `/relatorios/:id` e `/relatorios/:id/editar`. */
  '/relatorios': ['relatorio_vendas', 'relatorio_campanhas'],
  '/users': ['gestao_usuarios'],
  '/equipes': ['gestao_equipes'],
  '/metas': ['gestao_metas'],
  '/tipos-usuario': ['gestao_tipos_usuario'],
  '/chat-rules': ['regras_chatbot'],
  /* Atalhos de mensagem: a tela cria e edita os próprios. O escopo "da empresa" exige
     `atalhos.gerenciar` — a trava está na própria tela (e na API), não na rota. O "/" do Chat
     lê os atalhos com a chave `chat`, então quem não tem esta tela continua usando. */
  '/atalhos': ['atalhos'],
  '/products': ['gestao_produtos'],
  '/connections': ['gestao_conexoes'],
  '/apps': ['gestao_conexoes'],
  '/gerenciar-empresa': ['dias_funcionamento'],
  // Saiu da terceira aba de Gerenciar Empresa; herda a mesma exigência.
  '/pagina-de-captura': ['dias_funcionamento'],
  '/empresas': ['gestao_empresas'],
  '/gerenciar-planos': ['gerenciar_planos'],
  /* Mensagens é da área Sistema: consumo e custo de TODAS as empresas, do
     operador da plataforma. A chave é a mesma que a API exige na
     GBCodeAdminPolicy, e ela já existe na base (permission_id 13). Deixar a
     rota fora deste mapa cairia no `return true` e mostraria o consumo de
     todos os clientes para qualquer usuário logado. */
  '/sistema/mensagens': ['gbcode_admin_sistema'],
  /* Sem esta linha a rota cairia no `return true` e abriria para todos. */
  '/sistema/funcionalidades': ['gbcode_admin_sistema'],
  /* Mesma trava: fora do mapa, a rota abriria as conversas do assistente de
     todas as empresas para qualquer usuário logado. */
  '/sistema/assistente': ['gbcode_admin_sistema'],
  /* Visão geral: a plataforma inteira em números. Mesma trava. */
  '/sistema/visao-geral': ['gbcode_admin_sistema'],
  /* Site: os leads do brikcrm.com.br e o endereço da entrada. Mesma trava. */
  '/sistema/site': ['gbcode_admin_sistema'],
  '/sistema/sugestoes': ['gbcode_admin_sistema'],
  /* Central de ajuda (gestão dos artigos e vídeos): o item do menu já exigia a chave, mas a rota
     ficava fora do mapa e abria pela URL para qualquer usuário. `/admin/help/new` e
     `/admin/help/edit/:id` herdam pelo caminho-mãe. */
  '/admin/help': ['gbcode_admin_sistema'],
  /* Meu plano: dado de cobrança. A chave só deixa VER a assinatura; pagar, trocar e cancelar
     continuam do Administrador (papel, AdministradorEmpresaPolicy na API). */
  '/plano': ['plano'],
  '/campos-personalizados': ['gestao_campos_personalizados'],
  '/motivos-perda': ['gestao_motivo_perda'],
  '/funil': ['gestao_funil'],
  '/tipos-contato': ['gestao_tipos_contato'],
  /* Tarefas padrão é configuração da empresa (o parâmetro mora em Minhas empresas):
     herda a mesma exigência. Não existe permissão própria na base, e rota sem
     chave ficaria aberta para todos. */
  '/tarefas-padrao': ['dias_funcionamento'],
  /* Tipos de tarefa alimenta o trilho e a tarefa manual: mesma exigência da
     tela irmã, pela mesma razão — não existe permissão própria na base. */
  '/tipos-tarefa': ['dias_funcionamento'],
  /* Modelos de documento é configuração da empresa (quem cria é o administrador,
     que a API exige na AdministradorEmpresaPolicy): herda a mesma exigência das
     telas irmãs. Não existe permissão própria na base, e rota sem chave ficaria
     aberta para todos. A funcionalidade `modelos` está em ROUTE_FEATURES. */
  '/modelos': ['dias_funcionamento'],
};

export const PUBLIC_ROUTES = [
  /* Home e assistente: todo usuário tem, e o alcance do dado é decidido na API,
     dentro de cada ferramenta. NÃO registrar em ROUTE_PERMISSIONS — com o
     fallback do caminho-mãe, `/assistente/12` herdaria a exigência e o usuário
     comum perderia a própria Home. */
  '/home',
  '/assistente',
  '/primeiros-passos',
  '/help',
  '/novidades',
  /* Anotações é ferramenta pessoal: a API já devolve só as do próprio
     usuário (mais as que alguém compartilhou), então não há dado de outro
     time exposto por não haver permissão.

     Está aqui em vez de no ROUTE_PERMISSIONS de propósito, e é temporário:
     a permissão `anotacoes` ainda NÃO existe na base. Pôr a rota no mapa
     antes de criar a permissão deixaria a tela inacessível para todo mundo
     — só o Administrador tem permissões hoje. Quando a permissão existir,
     é mover uma linha para o ROUTE_PERMISSIONS abaixo. */
  '/anotacoes',
];

export function hasPermission(userPermissions: string[], required: string[]): boolean {
  return required.some(p => userPermissions.includes(p));
}

export function getUserPermissions(): string[] {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.permissoes ?? [];
  } catch {
    return [];
  }
}

/** Espelho de Brik.Domain/Permissoes/CatalogoPermissoes.cs. Não renomear: a
 *  string é a do banco e do token. Use estas constantes em vez de string solta. */
export const PERMISSOES = {
  chat: 'chat',
  fluxoVendas: 'fluxo_vendas',
  conversasTodos: 'conversas.todos',
  contatos: 'contatos',
  contatosGerenciar: 'contatos.gerenciar',
  assinaturas: 'assinaturas',
  assinaturasGerenciar: 'assinaturas.gerenciar',
  agenda: 'agenda',
  atalhos: 'atalhos',
  atalhosGerenciar: 'atalhos.gerenciar',
  painelOperacional: 'painel_operacional',
  gestaoVendasVendedor: 'gestao_vendas_vendedor',
  gestaoVendas: 'gestao_vendas',
  gestaoMetas: 'gestao_metas',
  relatorioVendas: 'relatorio_vendas',
  relatorioCampanhas: 'relatorio_campanhas',
  marketingDisparador: 'marketing_disparador',
  marketingDisparadorGerenciar: 'marketing_disparador.gerenciar',
  marketingTemplates: 'marketing_templates',
  marketingTemplatesGerenciar: 'marketing_templates.gerenciar',
  anuncios: 'anuncios',
  anunciosGerenciar: 'anuncios.gerenciar',
  gestaoUsuarios: 'gestao_usuarios',
  gestaoTiposUsuario: 'gestao_tipos_usuario',
  gestaoEquipes: 'gestao_equipes',
  gestaoProdutos: 'gestao_produtos',
  gestaoMotivoPerda: 'gestao_motivo_perda',
  gestaoTiposContato: 'gestao_tipos_contato',
  gestaoCamposPersonalizados: 'gestao_campos_personalizados',
  regrasChatbot: 'regras_chatbot',
  gestaoConexoes: 'gestao_conexoes',
  plano: 'plano',
  diasFuncionamento: 'dias_funcionamento',
  gbcodeAdminSistema: 'gbcode_admin_sistema',
  gestaoEmpresas: 'gestao_empresas',
  gerenciarPlanos: 'gerenciar_planos',
} as const;

/** Atalho para uma chave só. Não trata o admin à parte: o `Auth/Me` do
 *  administrador já traz todas as chaves não-sysadmin. */
export const temPermissao = (chave: string): boolean => hasPermission(getUserPermissions(), [chave]);

/** `TipoUsuarioCodigoEnum` da API. É o marcador NÃO configurável do papel —
 *  diferente de `role` (id do tipo, que é por empresa) e de
 *  `tipoUsuarioDescricao` (o nome, que a empresa edita). */
export const CODIGO_ADMINISTRADOR = 1;

/**
 * "É administrador da empresa?" — pergunta de PAPEL, não de permissão, e por
 * isso não está no catálogo de `permissoes`. O claim é condicional na API
 * (usuário sem tipo definido não o tem), então **ausência significa "não é"**,
 * nunca erro: um cadastro incompleto não pode derrubar a tela.
 */
export function ehAdministradorDaEmpresa(): boolean {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Number(user?.tipoUsuarioCodigo) === CODIGO_ADMINISTRADOR;
  } catch {
    return false;
  }
}

/* ── Funcionalidades (Sistema › Funcionalidades) ──────────────────────────
   A pergunta que fica ACIMA da permissão: "a plataforma oferece isto a esta
   empresa?". Quem decide é a GB Code, e a API manda a resposta pronta em
   `Auth/Me.funcionalidades` (só as ligadas). Desligada, a tela some mesmo com
   a permissão marcada; ligada, a pessoa ainda precisa da permissão.
   Chaves: Brik.Domain/Funcionalidades/CatalogoFuncionalidades.cs (API).
   Desenho: docs/designsystem/propostas/admin/fase-6-funcionalidades.html */

/** Rota → funcionalidade. Filha herda da mãe, como no ROUTE_PERMISSIONS. */
export const ROUTE_FEATURES: Record<string, string> = {
  '/anuncios': 'anuncios',
  '/modelos': 'modelos',
};

/** `null` = o `user` salvo não tem o campo (sessão de antes das flags, ou API
 *  antiga no ar). Nesse caso vale o comportamento de antes: tudo ligado. A API
 *  continua recusando o que estiver desligado, então o pior caso é um botão
 *  que responde "não disponível", nunca uma tela que some sem motivo. */
function getUserFuncionalidades(): string[] | null {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Array.isArray(user?.funcionalidades) ? user.funcionalidades : null;
  } catch {
    return null;
  }
}

export function temFuncionalidade(chave: string): boolean {
  const ligadas = getUserFuncionalidades();
  return ligadas === null || ligadas.includes(chave);
}

function funcionalidadeDaRota(path: string): string | undefined {
  const partes = path.split('/').filter(Boolean);
  for (let i = partes.length; i > 0; i--) {
    const chave = ROUTE_FEATURES['/' + partes.slice(0, i).join('/')];
    if (chave) return chave;
  }
  return undefined;
}

/** A rota depende de uma funcionalidade desligada para a empresa? */
export function rotaLiberadaPorFuncionalidade(path: string): boolean {
  const funcionalidade = funcionalidadeDaRota(path);
  return !funcionalidade || temFuncionalidade(funcionalidade);
}

export function canAccessRoute(path: string, userPermissions: string[]): boolean {
  // Antes de tudo, inclusive das rotas públicas: funcionalidade desligada fecha a tela para todos.
  if (!rotaLiberadaPorFuncionalidade(path)) return false;

  if (PUBLIC_ROUTES.some(r => path.startsWith(r))) return true;

  /* A busca era só exata, então toda rota de detalhe ficava aberta:
     `/chat-rules/12` e `/pipeline/deal/5` não são chaves do mapa e caíam
     no `return true`. Agora, não achando a rota, sobe pelos segmentos até
     achar a página-mãe e herda a permissão dela.

     Só muda o resultado para rotas filhas de uma chave existente — que é
     exatamente onde faltava. `/admin/help/new` continua sem exigência
     (nem `/admin` nem `/admin/help` são chaves). */
  const partes = path.split('/').filter(Boolean);
  for (let i = partes.length; i > 0; i--) {
    const required = ROUTE_PERMISSIONS['/' + partes.slice(0, i).join('/')];
    if (required) return hasPermission(userPermissions, required);
  }

  return true;
}

export function getFirstAccessibleRoute(userPermissions: string[]): string {
  const orderedRoutes = [
    /* A Home vem primeiro: é pública e serve para qualquer perfil, então quem
       não alcança nenhuma tela de operação cai numa tela útil em vez do fundo
       da lista. */
    '/home',
    '/monitoramento',
    '/dashboard',
    '/chat',
    '/pipeline',
    '/contacts',
    '/agenda',
    '/campanhas',
    '/users',
    '/primeiros-passos',
  ];
  for (const route of orderedRoutes) {
    if (canAccessRoute(route, userPermissions)) return route;
  }
  return '/primeiros-passos';
}
